/**
 * The single HTTP entry point.
 *
 * Everything the API contract promises (PRD 19) is implemented once, here:
 * problem-details errors, correlation IDs, idempotency keys, optimistic
 * concurrency, and the 402/403/409/429 distinctions the UI renders differently.
 *
 * Feature code calls `api.get` / `api.post` and never touches `fetch`.
 */

export const API_BASE = '/api/v1';

/** RFC 7807 problem details, as returned by `AllExceptionsFilter`. */
export interface ProblemDetails {
  type?: string;
  title: string;
  status: number;
  detail?: string;
  instance?: string;
  /** Field-level validation failures, keyed by property path. */
  errors?: Record<string, string[]>;
  /** Present on 402: what the plan allows and where to upgrade. */
  used?: number;
  limit?: number;
  resetsAt?: string;
  upgradeRoute?: string;
  /** Present on 429. */
  retryAfterSeconds?: number;
  correlationId?: string;
  /**
   * Stable machine-readable slug. Branch on this, never on the human text -
   * a copy change must not be a breaking change.
   */
  code?: string;
  /**
   * Present on 403 from the policy service (RBAC-003): which class of rule
   * refused - `missing_permission`, `branch_not_granted`, `not_own_record`,
   * `staff_suspended`, `reauth_required`, ...
   */
  reason?: string;
  /** Present on 403 `reauth_required` (AUTH-012). */
  requiredWithinMinutes?: number;
  /** Present on a failed MFA challenge: guesses left before the token dies. */
  attemptsRemaining?: number;
  /**
   * Present on 422 `ACTIVE_ENCOUNTER_LIMIT`: the consultation already open.
   *
   * The flow boards are scoped to the current clinic day, so the encounter
   * blocking a doctor may be from an earlier day and reachable from no screen -
   * which is why the refusal carries its id rather than only complaining.
   */
  blockingAppointmentId?: string;
  blockingStartedAt?: string;
}

/**
 * A failed request.
 *
 * Carries the parsed problem details rather than a string, because the UI
 * branches on `status`: 402 renders SubscriptionBlocked, 403 renders
 * PermissionDenied, 409 renders ConflictNotice. Collapsing them into one
 * message would make those states unreachable.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly problem: ProblemDetails;
  readonly correlationId: string | undefined;

  constructor(problem: ProblemDetails, correlationId?: string) {
    super(problem.detail || problem.title || `HTTP ${problem.status}`);
    this.name = 'ApiError';
    this.status = problem.status;
    this.problem = problem;
    this.correlationId = correlationId ?? problem.correlationId;
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
  get isForbidden(): boolean {
    return this.status === 403;
  }
  /** Subscription or plan restriction. Commercial, not administrative. */
  get isSubscriptionBlocked(): boolean {
    return this.status === 402;
  }
  /** Duplicate, slot clash, idempotency mismatch, or stale version. */
  get isConflict(): boolean {
    return this.status === 409;
  }
  get isValidation(): boolean {
    return this.status === 400 || this.status === 422;
  }
  get isRateLimited(): boolean {
    return this.status === 429;
  }
}

export interface RequestOptions {
  /** Query parameters. Undefined and null entries are dropped, not sent empty. */
  query?: Record<string, string | number | boolean | undefined | null>;
  /**
   * Replay protection for booking, checkout, payments, refunds, messaging,
   * imports and exports (PRD 19.1). Generate once per user intent and reuse it
   * across retries - a new key on retry defeats the whole mechanism.
   */
  idempotencyKey?: string;
  /**
   * Aggregate version for optimistic concurrency. Sent as `If-Match`; a stale
   * value returns 409 rather than overwriting a concurrent edit.
   */
  version?: number;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /**
   * `'text'` returns the raw body instead of parsed JSON, for endpoints that
   * return a rendered document. Failures are unaffected: an error response is
   * still problem-details JSON and still throws `ApiError`.
   */
  responseType?: 'json' | 'text' | 'blob';
}

let accessToken: string | null = null;
let onUnauthenticated: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Registered by the auth provider so a 401 anywhere lands on the login screen. */
export function setUnauthenticatedHandler(handler: () => void): void {
  onUnauthenticated = handler;
}

// ---------------------------------------------------------------------------
// Refresh token
// ---------------------------------------------------------------------------

const REFRESH_KEY = 'medos.refresh';

/**
 * Where the refresh token lives, and why that is a compromise.
 *
 * The access token stays in memory, which is right: it never reaches storage, so
 * an XSS payload cannot read it after the fact. The refresh token has to survive
 * a page reload or every F5 in a clinic is a re-login, and the server currently
 * returns it in the response body rather than setting an httpOnly cookie.
 *
 * `localStorage` is therefore the honest choice available today, and it is
 * readable by injected script. Moving the refresh token to an httpOnly,
 * SameSite=Strict cookie is the correct fix; it is a server change and is not
 * built. Recorded in the AUTH PR rather than left as a silent decision.
 */
export function setRefreshToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(REFRESH_KEY, token);
    else localStorage.removeItem(REFRESH_KEY);
  } catch {
    // Private-mode Safari throws on setItem. The session still works for this
    // tab; it just will not survive a reload.
  }
}

export function getRefreshToken(): string | null {
  try {
    return localStorage.getItem(REFRESH_KEY);
  } catch {
    return null;
  }
}

/** In flight, so ten concurrent 401s produce one refresh rather than ten. */
let refreshInFlight: Promise<boolean> | null = null;

/**
 * Exchanges the stored refresh token for a new pair.
 *
 * Returns false for every failure - expired, reused, revoked, or the session
 * went idle - and clears the stored token so the next call does not retry a
 * credential the server has already rejected.
 */
export async function refreshSession(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;

  refreshInFlight = (async () => {
    try {
      const response = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!response.ok) {
        setAccessToken(null);
        setRefreshToken(null);
        return false;
      }
      const tokens = (await response.json()) as { accessToken: string; refreshToken: string };
      setAccessToken(tokens.accessToken);
      setRefreshToken(tokens.refreshToken);
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${API_BASE}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.append(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

/**
 * Correlation ID for one user action.
 *
 * Generated client-side so the ID exists even when the request never reaches
 * the server (network failure), which is exactly the case support most needs to
 * trace. `crypto.randomUUID` is available in every browser the matrix covers;
 * the fallback keeps unit tests running under jsdom without it.
 */
function newCorrelationId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `c-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options: RequestOptions = {},
  /**
   * Internal. A 401 is retried once after a token refresh; the retry is marked
   * so a second 401 cannot loop.
   */
  isRetry = false,
): Promise<T> {
  const correlationId = newCorrelationId();

  const headers: Record<string, string> = {
    Accept: 'application/json',
    // The language the UI is actually showing, not the one the browser was
    // installed in.
    //
    // The server renders some text itself — notification titles and bodies,
    // their relative timestamps — and picks the language from `Accept-Language`
    // above the user's stored preference, on the stated reasoning that someone
    // who just switched the UI expects the next poll to follow. That was never
    // true from here: the language toggle writes `medos.locale`, and Chrome
    // keeps sending whatever locale it was installed with. So an Arabic clinic
    // on an English-locale machine read an Arabic interface whose notification
    // drawer was entirely English, down to "6 hours ago" reversed by RTL.
    //
    // Sending it makes the server's rule mean what it says. Read from the DOM
    // rather than the i18n context because this module is not a component and
    // `I18nProvider` already mirrors the locale onto <html lang>.
    'Accept-Language': document.documentElement.lang || 'ar',
    'X-Correlation-Id': correlationId,
    ...options.headers,
  };

  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey;
  if (typeof options.version === 'number') headers['If-Match'] = String(options.version);

  let payload: BodyInit | undefined;
  if (body instanceof FormData) {
    // Deliberately no Content-Type: the browser must set the multipart boundary.
    payload = body;
  } else if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    payload = JSON.stringify(body);
  }

  const response = await fetch(buildUrl(path, options.query), {
    method,
    headers,
    body: payload,
    signal: options.signal,
    // Refresh tokens live in an httpOnly cookie; the access token is a header.
    credentials: 'same-origin',
  });

  const serverCorrelationId = response.headers.get('X-Correlation-Id') ?? correlationId;

  if (response.status === 204) return undefined as T;

  const contentType = response.headers.get('Content-Type') ?? '';
  const isJson = contentType.includes('json');

  if (!response.ok) {
    let problem: ProblemDetails;
    if (isJson) {
      const parsed = (await response.json().catch(() => null)) as Partial<ProblemDetails> | null;
      problem = {
        title: parsed?.title ?? response.statusText,
        status: parsed?.status ?? response.status,
        ...parsed,
      };
    } else {
      problem = { title: response.statusText, status: response.status };
    }

    if (response.status === 401) {
      // An expired 15-minute access token is the overwhelmingly common cause,
      // and signing the user out for it would log a clinic out four times an
      // hour. Try the refresh token once, then replay the original request.
      //
      // `SESSION_IDLE_TIMEOUT` is excluded deliberately: the server has decided
      // the session is over, and refreshing would defeat AUTH-010.
      const canRetry =
        !isRetry && !options.signal?.aborted && problem.code !== 'SESSION_IDLE_TIMEOUT';

      if (canRetry && (await refreshSession())) {
        return request<T>(method, path, body, options, true);
      }

      onUnauthenticated?.();
    }

    throw new ApiError(problem, serverCorrelationId);
  }

  if (options.responseType === 'text') return (await response.text()) as T;
  if (options.responseType === 'blob') return (await response.blob()) as T;

  return isJson ? ((await response.json()) as T) : (undefined as T);
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>('GET', path, undefined, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('POST', path, body, options),
  /**
   * A response that is a document rather than data.
   *
   * The clinical print templates (VIS-020..023) are rendered server-side, so
   * the document number and the amendment indicator are minted where they
   * cannot be forged. The response body is HTML; error responses are still
   * problem-details JSON and still become an `ApiError`, so the safety gate's
   * 422 on a blocked print is handled exactly like every other refusal.
   */
  postText: (path: string, body?: unknown, options?: RequestOptions) =>
    request<string>('POST', path, body, { ...options, responseType: 'text' }),
  getText: (path: string, options?: RequestOptions) =>
    request<string>('GET', path, undefined, { ...options, responseType: 'text' }),
  /**
   * Bytes behind the session — an imaging study, a scan, an attachment.
   *
   * These endpoints take a short-lived signed token AND sit behind the auth and
   * tenant guards, deliberately: the token authorises the *file*, the session
   * authorises the *caller*. So a signed link can never be opened by navigating
   * to it — a top-level navigation carries no Authorization header and no
   * X-Tenant-ID, and the request is refused before the signature is ever read.
   * The bytes have to be fetched with the session and handed to the browser as
   * a blob, which is what this exists for.
   */
  getBlob: (path: string, options?: RequestOptions) =>
    request<Blob>('GET', path, undefined, { ...options, responseType: 'blob' }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PATCH', path, body, options),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PUT', path, body, options),
  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>('DELETE', path, undefined, options),
};

/** Stable cursor pagination envelope used by every list endpoint. */
export interface Page<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}
